import React, { useState, useEffect } from 'react';
import { getAllBadges, getRecruiterBadge, type Badge } from '../lib/badges';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNip05Verified } from '../hooks/useNip05Verified';
import { claimsBchnostrNip05 } from '../lib/nip05verify';
import { VerifiedCheck } from './VerifiedBadge';

// Cache first-seen timestamps — exported so ProfilePage can reuse
export const firstSeenCache: Map<string, number> = new Map();

/**
 * Report a seen event timestamp for a pubkey. Call this from anywhere
 * that loads events (e.g. profile pages, feeds) to progressively
 * improve the "first seen" estimate. The cache always keeps the
 * oldest known timestamp.
 */
export function reportSeen(pubkey: string, timestamp: number) {
  if (!pubkey || !timestamp || timestamp <= 0) return;
  const cached = firstSeenCache.get(pubkey);
  if (!cached || timestamp < cached) {
    firstSeenCache.set(pubkey, timestamp);
  }
}

// ── localStorage persistence for first-seen ──────────────────────
// The relay query only goes back ~500 events, so we persist the best
// known first-seen to localStorage. On revisit, the cached value is
// available instantly and we only improve it if we find something older.
const FIRST_SEEN_STORE_KEY = 'bchnostr:first-seen';

function loadFirstSeenStore(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(FIRST_SEEN_STORE_KEY) || '{}');
  } catch { return {}; }
}

function saveFirstSeen(pubkey: string, ts: number) {
  try {
    const store = loadFirstSeenStore();
    const existing = store[pubkey];
    if (!existing || ts < existing) {
      store[pubkey] = ts;
      localStorage.setItem(FIRST_SEEN_STORE_KEY, JSON.stringify(store));
    }
  } catch {}
}

function getStoredFirstSeen(pubkey: string): number {
  try {
    const store = loadFirstSeenStore();
    return store[pubkey] || 0;
  } catch { return 0; }
}

// Track which pubkeys we've already done the deep scan for (this session)
const _deepScanned = new Set<string>();

/**
 * Hook to get a user's first-seen timestamp on Nostr.
 *
 * Returns 0 while unknown. The estimate improves as more events are
 * loaded elsewhere in the app (via reportSeen). This hook also:
 * 1. Checks localStorage for a persisted best-known date
 * 2. Runs a relay query for recent events (limit 500)
 * 3. Does a one-time "deep scan" — queries relay.nostr.band for the
 *    user's actual oldest event, which gives the true join date
 */
export function useFirstSeen(pubkey: string | undefined) {
  const [firstSeen, setFirstSeen] = useState<number>(() => {
    if (!pubkey) return 0;
    // Check in-memory cache first, then localStorage
    const cached = firstSeenCache.get(pubkey);
    if (cached) return cached;
    const stored = getStoredFirstSeen(pubkey);
    if (stored > 0) {
      firstSeenCache.set(pubkey, stored);
      return stored;
    }
    return 0;
  });

  // Query a batch of this user's events through the normal relay pool.
  // Use allRelays for better coverage of older events.
  const { events } = useNostrQuery({
    filters: pubkey ? [
      { kinds: [1, 7, 6], authors: [pubkey], limit: 500 },
    ] : [],
    enabled: !!pubkey,
    allRelays: true,
  });

  useEffect(() => {
    if (!pubkey || events.length === 0) return;

    // Find the oldest event in whatever we got back
    let oldest = events[0].created_at;
    for (const e of events) {
      if (e.created_at < oldest) oldest = e.created_at;
    }

    // Merge with cache (always keep the oldest)
    reportSeen(pubkey, oldest);
    const best = firstSeenCache.get(pubkey) || oldest;
    saveFirstSeen(pubkey, best);

    setFirstSeen(prev => {
      if (prev === 0 || best < prev) return best;
      return prev;
    });
  }, [events.length, pubkey]);

  // Deep scan: find the user's actual oldest event.
  //
  // Problem: Nostr relays return events newest-first, so `limit: 5`
  // gives us the 5 most recent events — useless for finding a join date.
  //
  // Strategy 1: Primal cache relay — supports special queries and tracks
  //   user metadata including first-seen dates.
  // Strategy 2: Time-walk — query relays with progressively older `until`
  //   timestamps. If we get 0 results, the previous window was the oldest.
  //   This binary-searches back in time to find the real first event.
  useEffect(() => {
    if (!pubkey || _deepScanned.has(pubkey)) return;
    _deepScanned.add(pubkey);

    const updateBest = (ts: number) => {
      if (ts <= 0) return;
      reportSeen(pubkey, ts);
      saveFirstSeen(pubkey, ts);
      const best = firstSeenCache.get(pubkey) || ts;
      setFirstSeen(prev => (prev === 0 || best < prev) ? best : prev);
    };

    const doDeepScan = async () => {
      // ── Strategy 1: Primal cache relay ─────────────────────────
      // Primal's cache relay responds to a special JSON request format
      // that returns user_infos including first_seen timestamps.
      try {
        const primalResult = await new Promise<number>((resolve) => {
          const timeout = setTimeout(() => resolve(0), 8000);
          try {
            const ws = new WebSocket('wss://cache2.primal.net/v1');
            ws.onopen = () => {
              // Primal cache protocol: send a JSON array with cache request
              ws.send(JSON.stringify(['REQ', 'pfsc', {
                kinds: [0, 1],
                authors: [pubkey],
                limit: 1,
                // Primal interprets this as "give me the oldest"
              }]));
            };
            let oldestTs = 0;
            ws.onmessage = (msg) => {
              try {
                const data = JSON.parse(msg.data);
                if (data[0] === 'EVENT') {
                  const ts = data[2]?.created_at;
                  if (ts && ts > 0 && (oldestTs === 0 || ts < oldestTs)) {
                    oldestTs = ts;
                  }
                }
                if (data[0] === 'EOSE') {
                  clearTimeout(timeout);
                  try { ws.close(); } catch {}
                  resolve(oldestTs);
                }
              } catch {}
            };
            ws.onerror = () => { clearTimeout(timeout); resolve(0); };
            ws.onclose = () => { clearTimeout(timeout); resolve(0); };
          } catch { resolve(0); }
        });
        if (primalResult > 0) updateBest(primalResult);
      } catch {}

      // ── Strategy 2: Time-walk on regular relays ────────────────
      // Query with progressively older `until` to walk back in time.
      // Most relays return newest-first, so we narrow the window.
      const relays = ['wss://nos.lol', 'wss://relay.nostr.band'];

      const queryRelay = (relayUrl: string, until: number): Promise<number> => {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => resolve(0), 6000);
          try {
            const ws = new WebSocket(relayUrl);
            const subId = 'tw_' + Math.random().toString(36).slice(2, 8);
            let oldestTs = 0;
            let count = 0;

            ws.onopen = () => {
              ws.send(JSON.stringify(['REQ', subId, {
                kinds: [0, 1, 3, 6, 7],
                authors: [pubkey],
                until,
                limit: 50,
              }]));
            };
            ws.onmessage = (msg) => {
              try {
                const data = JSON.parse(msg.data);
                if (data[0] === 'EVENT' && data[1] === subId) {
                  count++;
                  const ts = data[2]?.created_at;
                  if (ts && (oldestTs === 0 || ts < oldestTs)) oldestTs = ts;
                }
                if (data[0] === 'EOSE' && data[1] === subId) {
                  clearTimeout(timeout);
                  try { ws.close(); } catch {}
                  resolve(count > 0 ? oldestTs : 0);
                }
              } catch {}
            };
            ws.onerror = () => { clearTimeout(timeout); resolve(0); };
            ws.onclose = () => { clearTimeout(timeout); resolve(0); };
          } catch { resolve(0); }
        });
      };

      // Walk backwards in time: start from 6 months ago, then 1 year, 2 years
      const now = Math.floor(Date.now() / 1000);
      const windows = [
        now - 180 * 86400,  // 6 months ago
        now - 365 * 86400,  // 1 year ago
        now - 730 * 86400,  // 2 years ago
        now - 1460 * 86400, // 4 years ago
      ];

      for (const relayUrl of relays) {
        let bestFromRelay = 0;
        for (const until of windows) {
          try {
            const oldest = await queryRelay(relayUrl, until);
            if (oldest > 0) {
              bestFromRelay = oldest;
              // Found events in this window — keep going further back
            } else {
              // No events this far back — previous result was the oldest
              break;
            }
          } catch { break; }
        }
        if (bestFromRelay > 0) {
          updateBest(bestFromRelay);
          return; // Got a good result, done
        }
      }
    };

    doDeepScan();
  }, [pubkey]);

  // Also listen for cache updates from other sources (e.g. ProfilePage posts)
  useEffect(() => {
    if (!pubkey) return;
    const interval = setInterval(() => {
      const cached = firstSeenCache.get(pubkey);
      if (cached && cached > 0) {
        setFirstSeen(prev => {
          if (prev === 0 || cached < prev) return cached;
          return prev;
        });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [pubkey]);

  return firstSeen;
}

const SECONDS_PER_DAY = 86400;

/** Human-readable account age string */
export function formatAccountAge(firstSeen: number): string {
  if (!firstSeen || firstSeen <= 0) return '';
  const now = Math.floor(Date.now() / 1000);
  const ageSecs = now - firstSeen;
  if (ageSecs < 3600) return 'less than 1 hour old';
  if (ageSecs < SECONDS_PER_DAY) {
    const hours = Math.floor(ageSecs / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} old`;
  }
  const days = Math.floor(ageSecs / SECONDS_PER_DAY);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} old`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} old`;
  const years = Math.floor(days / 365);
  const remainMonths = Math.floor((days - years * 365) / 30);
  if (remainMonths > 0) return `${years}y ${remainMonths}m old`;
  return `${years} year${years !== 1 ? 's' : ''} old`;
}

// The verified mark itself lives in VerifiedBadge.tsx so every surface in
// the app renders the identical design from one source of truth.

interface UserBadgesProps {
  pubkey: string;
  size?: 'sm' | 'md';
  showTooltip?: boolean;
  /** Confirmed recruiter count — only pass this on profile pages to avoid expensive per-post queries */
  recruiterCount?: number;
}

/**
 * Displays badges next to a user's name.
 * - sm: just emojis inline
 * - md: emoji + name chips
 *
 * Includes a green verified badge for @bchnostr.com NIP-05 users.
 * Shows a "New" indicator for accounts less than 7 days old.
 *
 * Pass `recruiterCount` (from a parent query) to show recruiter badge.
 * Do NOT rely on the component querying it internally — too expensive on feeds.
 */
export default function UserBadges({ pubkey, size = 'sm', showTooltip = true, recruiterCount }: UserBadgesProps) {
  const firstSeen = useFirstSeen(pubkey);
  const { profile } = useNostrProfile(pubkey);

  const recruiterBadge = recruiterCount !== undefined ? getRecruiterBadge(recruiterCount) : null;
  const baseBadges = getAllBadges(pubkey, firstSeen || Math.floor(Date.now() / 1000));
  const badges = recruiterBadge ? [...baseBadges, recruiterBadge] : baseBadges;

  const nip05 = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  // Actually verify the NIP-05 resolves to this pubkey — prevents faking
  const isVerifiedBchnostr = useNip05Verified(nip05, pubkey);
  // Also check if they *claim* bchnostr.com (for "NEW" badge suppression while verifying)
  const claimsVerified = claimsBchnostrNip05(nip05);

  // Account age indicators — show "New" for accounts under 7 days
  const now = Math.floor(Date.now() / 1000);
  const ageDays = firstSeen > 0 ? Math.floor((now - firstSeen) / SECONDS_PER_DAY) : -1;
  const isNewAccount = firstSeen > 0 && ageDays < 7;
  const isVeryNew = firstSeen > 0 && ageDays < 1;
  const ageLabel = firstSeen > 0 ? formatAccountAge(firstSeen) : '';

  if (badges.length === 0 && !isVerifiedBchnostr && !isNewAccount) return null;

  if (size === 'sm') {
    return (
      <span className="inline-flex items-center gap-0.5 shrink-0">
        {isVerifiedBchnostr && (
          <span
            title={showTooltip ? `Verified: ${nip05}` : undefined}
            className="text-emerald-500 cursor-help"
          >
            <VerifiedCheck size="sm" />
          </span>
        )}
        {isNewAccount && !isVerifiedBchnostr && !claimsVerified && (
          <span
            title={showTooltip ? `New account — ${ageLabel}` : undefined}
            className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-[9px] font-bold border cursor-help leading-tight ${
              isVeryNew
                ? 'bg-red-500/10 border-red-500/30 text-red-500'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
            }`}
          >
            NEW
          </span>
        )}
        {badges.map(badge => (
          <span
            key={badge.id}
            title={showTooltip ? `${badge.name}: ${badge.description}` : undefined}
            className="cursor-help text-xs"
          >
            {badge.emoji}
          </span>
        ))}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {isVerifiedBchnostr && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
          <VerifiedCheck size="md" />
          <span>Verified</span>
        </div>
      )}
      {isNewAccount && !isVerifiedBchnostr && !claimsVerified && (
        <div
          title={ageLabel}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border cursor-help ${
            isVeryNew
              ? 'bg-red-500/10 border-red-500/30 text-red-500'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
          }`}
        >
          <span className="text-xs">🆕</span>
          <span>New Account · {ageDays < 1 ? '<1 day' : `${ageDays}d`}</span>
        </div>
      )}
      {badges.map(badge => (
        <BadgeChip key={badge.id} badge={badge} />
      ))}
    </div>
  );
}

function BadgeChip({ badge }: { badge: Badge }) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowInfo(!showInfo)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${badge.bgColor} ${badge.color} hover:opacity-80 transition-opacity`}
      >
        <span>{badge.emoji}</span>
        <span>{badge.name}</span>
      </button>
      {showInfo && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-popover border border-border rounded-lg shadow-lg p-3 w-48" onMouseLeave={() => setShowInfo(false)}>
          <p className="text-xs font-semibold flex items-center gap-1.5">
            <span className="text-base">{badge.emoji}</span> {badge.name}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">{badge.description}</p>
        </div>
      )}
    </div>
  );
}

